import { useMemo } from "react";
import { useApolloClient } from "@apollo/client/react";
import { useQuery } from "@tanstack/react-query";
import { useUniversalAuth } from "@wtc/ui";
import {
  GetCeoHealthCompaniesDocument,
  GetCeoHealthDealsDocument,
  GetCeoHealthTimeEntriesDocument,
  GetCeoHealthUsersDocument,
  GetCeoHealthCompaniesQuery,
  GetCeoHealthDealsQuery,
  GetCeoHealthTimeEntriesQuery,
  GetCeoHealthUsersQuery,
} from "@/src/gql/graphql";
import { HealthMetrics } from "@/lib/types/database/deals";
import {
  calculateHealthMetrics,
  type HealthCompanyNode,
  type HealthDealNode,
  type HealthTimeEntryNode,
  type HealthUserNode,
} from "./health-metrics-calculator";
import { fetchAllGraphqlNodes } from "./paginate";

// ============================================================================
// CEO HEALTH METRICS HOOK (GraphQL Version)
// ============================================================================
//
// PURPOSE: Calculate delivery health metrics for CEO dashboard using GraphQL
//
// METRICS:
// - Active Projects (R/Y/G): Count of companies by project health status
// - Margin %: Average margin across active deals
// - Utilization %: Average team utilization
//
// Each of the four collections is paged to completion via fetchAllGraphqlNodes
// (the prior first:1000 calls truncated rated-company detection against 5,858
// companies). The companies query is now also RATED-only server-side.
// ============================================================================

interface UseCeoHealthMetricsGraphQLOptions {
  enabled?: boolean;
  dateFrom?: string;
  dateTo?: string;
}

interface HealthData {
  companies: HealthCompanyNode[];
  deals: HealthDealNode[];
  timeEntries: HealthTimeEntryNode[];
  users: HealthUserNode[];
}

const EMPTY_DATA: HealthData = {
  companies: [],
  deals: [],
  timeEntries: [],
  users: [],
};

export function useCeoHealthMetricsGraphQL(
  options: UseCeoHealthMetricsGraphQLOptions = {}
) {
  const { isAuthenticated } = useUniversalAuth();
  const { enabled = true, dateFrom, dateTo } = options;
  const client = useApolloClient();

  // Validate date range
  if (dateFrom && dateTo) {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    if (from > to) {
      throw new Error("Start date must be before end date");
    }
  }

  // Calculate date 30 days ago for time entries query (or use dateFrom if provided)
  const timeEntriesDateFrom = useMemo(() => {
    if (dateFrom) {
      return dateFrom;
    }
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split("T")[0];
  }, [dateFrom]);

  const {
    data,
    isLoading,
    error,
  } = useQuery<HealthData>({
    queryKey: [
      "ceo-health-metrics",
      dateFrom ?? null,
      dateTo ?? null,
      timeEntriesDateFrom,
    ],
    enabled: isAuthenticated && enabled,
    queryFn: async () => {
      const [companies, deals, timeEntries, users] = await Promise.all([
        fetchAllGraphqlNodes<GetCeoHealthCompaniesQuery, HealthCompanyNode>(
          client,
          GetCeoHealthCompaniesDocument,
          { dateFrom, dateTo },
          (d) => d.companiesCollection,
        ),
        fetchAllGraphqlNodes<GetCeoHealthDealsQuery, HealthDealNode>(
          client,
          GetCeoHealthDealsDocument,
          { dateFrom, dateTo },
          (d) => d.dealsCollection,
        ),
        fetchAllGraphqlNodes<GetCeoHealthTimeEntriesQuery, HealthTimeEntryNode>(
          client,
          GetCeoHealthTimeEntriesDocument,
          { dateFrom: timeEntriesDateFrom, dateTo },
          (d) => d.time_entriesCollection,
        ),
        fetchAllGraphqlNodes<GetCeoHealthUsersQuery, HealthUserNode>(
          client,
          GetCeoHealthUsersDocument,
          {},
          (d) => d.app_usersCollection,
        ),
      ]);

      return { companies, deals, timeEntries, users };
    },
  });

  const resolved = data ?? EMPTY_DATA;

  // Days in the time-entries window — drives the utilization capacity so it
  // scales with the selected period (matches the window time entries were
  // fetched over: timeEntriesDateFrom → dateTo|today).
  const daysInPeriod = useMemo(() => {
    const from = new Date(timeEntriesDateFrom);
    const to = dateTo ? new Date(dateTo) : new Date();
    const diff = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24));
    return Number.isFinite(diff) && diff > 0 ? diff : 30;
  }, [timeEntriesDateFrom, dateTo]);

  const metrics = useMemo((): HealthMetrics => {
    return calculateHealthMetrics(
      resolved.companies,
      resolved.deals,
      resolved.timeEntries,
      resolved.users,
      daysInPeriod
    );
  }, [resolved, daysInPeriod]);

  return {
    metrics,
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    companies: resolved.companies,
    deals: resolved.deals,
    timeEntries: resolved.timeEntries,
    users: resolved.users,
  };
}
